// Group headers of a grouped list (phase 3 §4.3). The server orders the list
// by the group and says each item's group (item.group) and each shown
// group's size; the browser starts a section each time the group changes
// along the page, "(continued)" when the page starts inside a group.
import React from 'react';
import { useTranslation } from 'react-i18next';
import { fieldKey } from '../../fieldKeys';
import { selectLabel, uiLang, useVocabulary, vocabLabels } from '../../kinds';
import { placeName } from '../../locations';
import { PLACE_GROUP, TYPE_GROUP, YEAR_GROUP } from './ordering';

/**
 * The page's items cut where their group changes: [{key, value, label, count,
 * continued, items}]. Without a group, one segment holding everything.
 */
export const groupSegments = (items, group) => {
  if (!group) return [{ key: 'all', items }];
  const byValue = new Map((group.values || []).map((entry) => [entry.value ?? null, entry]));
  const segments = [];
  items.forEach((item) => {
    const value = item.group ?? null;
    const last = segments[segments.length - 1];
    if (last && last.value === value) {
      last.items.push(item);
      return;
    }
    const entry = byValue.get(value) || {};
    segments.push({
      key: `${segments.length}-${value ?? ''}`,
      value,
      label: entry.label ?? null,
      count: entry.count ?? null,
      continued: segments.length === 0 && !!group.continued,
      items: [item],
    });
  });
  return segments;
};

/** What a group is called: a type's name, a place's, a decade, a field's label for a code; "No …" without a value. */
export function GroupName({ info, value, label }) {
  const { t, i18n } = useTranslation();
  const lang = uiLang(i18n);
  const field = info.field || null;
  const vocabulary = useVocabulary(field && field.type === 'vocab' ? field.vocabulary : null);
  const key = info.key;
  if (value === null || value === undefined) {
    if (key === TYPE_GROUP) return t('collection.group.noType');
    if (key === PLACE_GROUP) return t('collection.group.noPlace');
    if (key === YEAR_GROUP) return t('collection.group.noYear');
    return t('collection.group.noValue', { name: field ? field.name : key });
  }
  if (key === TYPE_GROUP) {
    const type = (info.itemTypes || []).find((candidate) => String(candidate.id) === String(value));
    return type ? type.name : t('collection.group.otherType');
  }
  if (key === PLACE_GROUP) {
    const place = (info.places || []).find((candidate) => String(candidate.id) === String(value));
    return place ? placeName(t, place) : '…';
  }
  if (key === YEAR_GROUP) return t('collection.group.decadeOf', { decade: value });
  if (!field) return label || value;
  if (field.type === 'yesno') return t(value === '1' ? 'collection.group.yes' : 'collection.group.no', { name: field.name });
  if (field.type === 'vocab') return vocabLabels(vocabulary, [value], lang, field)[0];
  if (['select', 'radio', 'checkbox'].includes(field.type)) return String(selectLabel(field, value, lang));
  return label || value;
}

/** The group's line: its name, its size (read out as "N items"), "(continued)" at the top of a page. */
export function GroupTitle({ info, segment }) {
  const { t, i18n } = useTranslation();
  const number = (n) => new Intl.NumberFormat(i18n.resolvedLanguage || 'en').format(n);
  return (
    <>
      <span className="item-group__name"><GroupName info={info} value={segment.value} label={segment.label} /></span>
      {segment.continued && <span className="item-group__continued">{t('collection.group.continued')}</span>}
      {segment.count !== null && segment.count !== undefined && (
        <span className="item-group__count">
          <span aria-hidden="true">{number(segment.count)}</span>
          <span className="visually-hidden">{t('shell.itemCount', { count: segment.count })}</span>
        </span>
      )}
    </>
  );
}

/** The group's field, by key, among the collection's fields (null for @type, @place, @year). */
export const groupField = (key, fields) => (key && !key.startsWith('@')
  ? (fields || []).find((field) => fieldKey(field) === key) || null
  : null);
